import "../index.css";
import { PencilIcon } from "lucide-react";
import { useTranslation } from "react-i18next";
import type { IContractingCompany } from "@/types/companies-contractors";
import type { ILender } from "@/types/companies-lenders";
import { Card, Heading, IconButton, LabelValue, Row, Tooltip } from "@/ui-kit";
import { PhoneUtils } from "@/utils/phone";

type CompanyType = IContractingCompany | ILender;

type IProps = {
  company: Pick<
    CompanyType,
    | "name"
    | "phone"
    | "email"
    | "companyLogo"
    | "licenseNumber"
    | "projectCount"
    | "membersCount"
  >;
  onEdit?: () => void;
};

export const CompanyOverviewCard = ({ company, onEdit }: IProps) => {
  const { t } = useTranslation();
  const labelWidth = "80px";

  return (
    <Card className="profile-card">
      <Card.Body>
        <div className="profile-card__layout">
          <img
            className="profile-card__image"
            src={company.companyLogo}
            alt="Company logo"
          />
          <div className="profile-card__details">
            <Row gap="sm">
              <Heading level={3} tone="default">
                {company.name}
              </Heading>

              {onEdit && (
                <div className="profile-card__actions">
                  <Tooltip content={t("action.edit")} placement="bottom-end">
                    <IconButton
                      size="sm"
                      variant="ghost"
                      color="neutral"
                      content={<PencilIcon />}
                      onClick={onEdit}
                      aria-label={t("action.edit")}
                    />
                  </Tooltip>
                </div>
              )}
            </Row>

            <div className="profile-card__sections">
              <div className="profile-card__section">
                <LabelValue
                  label={t("label.phone")}
                  value={PhoneUtils.format(company.phone)}
                  labelWidth={labelWidth}
                  canCopy
                />
                <LabelValue
                  label={t("label.email")}
                  value={company.email}
                  labelWidth={labelWidth}
                  canCopy
                />
              </div>
              <div className="profile-card__section">
                {company.licenseNumber ? (
                  <LabelValue
                    label={t("label.license")}
                    value={company.licenseNumber}
                    labelWidth={labelWidth}
                    canCopy
                  />
                ) : null}
                {company.projectCount ? (
                  <LabelValue
                    label={t("label.projects")}
                    value={company.projectCount}
                    labelWidth={labelWidth}
                  />
                ) : null}
                {company.membersCount ? (
                  <LabelValue
                    label={t("label.members")}
                    value={company.membersCount}
                    labelWidth={labelWidth}
                  />
                ) : null}
              </div>
            </div>
          </div>
        </div>
      </Card.Body>
    </Card>
  );
};
